import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, MessageSquare, Clock, CheckCircle, XCircle, AlertCircle, Loader2, ArrowLeft } from 'lucide-react'
import { ticketsAPI } from '@/lib/api'
import { io } from 'socket.io-client'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'

const CATEGORIES = [
  { value: 'network', label: 'Netwerk' },
  { value: 'hardware', label: 'Hardware' },
  { value: 'gaming', label: 'Gaming' },
  { value: 'software', label: 'Software' },
  { value: 'other', label: 'Andere' }
]

const PRIORITIES = [
  { value: 'low', label: 'Laag', color: 'bg-blue-500' },
  { value: 'normal', label: 'Normaal', color: 'bg-green-500' },
  { value: 'high', label: 'Hoog', color: 'bg-orange-500' },
  { value: 'urgent', label: 'Urgent', color: 'bg-red-500' }
]

const STATUS_CONFIG = {
  open: { label: 'Open', icon: AlertCircle, color: 'text-blue-500' },
  in_progress: { label: 'Bezig', icon: Clock, color: 'text-yellow-500' },
  waiting_user: { label: 'Wacht op u', icon: MessageSquare, color: 'text-purple-500' },
  resolved: { label: 'Opgelost', icon: CheckCircle, color: 'text-green-500' },
  closed: { label: 'Gesloten', icon: XCircle, color: 'text-gray-500' }
}

export default function HelpdeskPage() {
  const navigate = useNavigate()
  const [tickets, setTickets] = useState([])
  const [loading, setLoading] = useState(true)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'other',
    priority: 'normal',
    location: ''
  })

  useEffect(() => {
    loadTickets()
  }, [])

  // Socket.IO for real-time updates
  useEffect(() => {
    const token = localStorage.getItem('auth_token')
    if (!token) {
      console.log('No token found, skipping socket connection')
      return
    }

    console.log('Setting up socket connection for user tickets...')
    const socket = io({
      auth: { token },
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    })

    socket.on('connect', () => {
      console.log('✅ Connected to tickets socket for user')
    })

    socket.on('connect_error', (error) => {
      console.error('❌ Socket connection error:', error)
    })

    // Listen for ticket updates that belong to this user
    socket.on('ticket-created', ({ ticket }) => {
      console.log('🎫 New ticket created:', ticket)
      loadTickets()
    })

    socket.on('ticket-updated', ({ ticket }) => {
      console.log('🔄 Ticket updated:', ticket)
      loadTickets()
    })

    socket.on('ticket-assigned', ({ ticket }) => {
      console.log('👤 Ticket assigned:', ticket)
      loadTickets()
    })

    socket.on('ticket-reply-added', ({ ticketId }) => {
      console.log('💬 New reply added to ticket:', ticketId)
      loadTickets()
    })

    socket.on('disconnect', () => {
      console.log('❌ Disconnected from tickets socket')
    })

    return () => {
      console.log('🔌 Cleaning up socket connection')
      socket.disconnect()
    }
  }, [])

  const loadTickets = async () => {
    try {
      setLoading(true)
      const response = await ticketsAPI.getTickets()
      setTickets(response.data.tickets || [])
    } catch (err) {
      console.error('Failed to load tickets:', err)
      setError('Kon tickets niet laden')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateTicket = async (e) => {
    e.preventDefault()
    setCreating(true)
    setError(null)

    try {
      await ticketsAPI.createTicket(formData)
      setSuccess('Ticket succesvol aangemaakt!')
      setCreateDialogOpen(false)
      setFormData({
        title: '',
        description: '',
        category: 'other',
        priority: 'normal',
        location: ''
      })
      loadTickets()

      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Failed to create ticket:', err)
      setError(err.response?.data?.error || 'Kon ticket niet aanmaken')
    } finally {
      setCreating(false)
    }
  }

  const getPriorityColor = (priority) => {
    return PRIORITIES.find(p => p.value === priority)?.color || 'bg-gray-500'
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-neon-green" />
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="hover:bg-muted"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Terug
            </Button>
          </div>
          <h1 className="text-4xl font-bold text-neon-green">Helpdesk</h1>
          <p className="text-muted-foreground mt-2">Vraag hulp aan ons helpdesk team</p>
        </div>

        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-neon-green text-background hover:bg-neon-green/90">
              <Plus className="h-4 w-4 mr-2" />
              Nieuw Ticket
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl bg-card border-neon">
            <DialogHeader>
              <DialogTitle className="text-neon-green">Nieuw Support Ticket</DialogTitle>
            </DialogHeader>

            <form onSubmit={handleCreateTicket} className="space-y-4">
              <div>
                <Label htmlFor="title">Onderwerp *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Korte omschrijving van het probleem"
                  required
                  className="bg-background border-neon"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="category">Categorie *</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                    <SelectTrigger className="bg-background border-neon">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="priority">Prioriteit *</Label>
                  <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                    <SelectTrigger className="bg-background border-neon">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map(pri => (
                        <SelectItem key={pri.value} value={pri.value}>{pri.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="location">Locatie (optioneel)</Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Bijv. Tafel 12, Zaal A"
                  className="bg-background border-neon"
                />
              </div>

              <div>
                <Label htmlFor="description">Beschrijving *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Beschrijf je probleem zo gedetailleerd mogelijk..."
                  rows={6}
                  required
                  className="bg-background border-neon"
                />
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                  Annuleren
                </Button>
                <Button type="submit" disabled={creating} className="bg-neon-green text-background hover:bg-neon-green/90">
                  {creating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Aanmaken...
                    </>
                  ) : (
                    'Ticket Aanmaken'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Success Message */}
      {success && (
        <Alert className="mb-6 border-neon-green bg-neon-green/10">
          <CheckCircle className="h-4 w-4 text-neon-green" />
          <AlertDescription className="text-neon-green">{success}</AlertDescription>
        </Alert>
      )}

      {/* Tickets List */}
      {tickets.length === 0 ? (
        <Card className="border-neon-purple/30">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <MessageSquare className="h-16 w-16 text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">Geen tickets</h3>
            <p className="text-muted-foreground text-center mb-6">
              Je hebt nog geen support tickets aangemaakt
            </p>
            <Button onClick={() => setCreateDialogOpen(true)} className="bg-neon-green text-background hover:bg-neon-green/90">
              <Plus className="h-4 w-4 mr-2" />
              Eerste Ticket Aanmaken
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {tickets.map(ticket => {
            const StatusIcon = STATUS_CONFIG[ticket.status]?.icon || AlertCircle
            const statusColor = STATUS_CONFIG[ticket.status]?.color || 'text-gray-500'

            return (
              <Card
                key={ticket.id}
                className="border-neon-purple/30 hover:border-neon-purple/60 transition-colors cursor-pointer"
                onClick={() => navigate(`/helpdesk/${ticket.id}`)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="text-xs">
                          #{ticket.id}
                        </Badge>
                        <div className={`h-2 w-2 rounded-full ${getPriorityColor(ticket.priority)}`} />
                        <span className="text-xs text-muted-foreground">
                          {PRIORITIES.find(p => p.value === ticket.priority)?.label}
                        </span>
                        <span className="text-xs text-muted-foreground">•</span>
                        <span className="text-xs text-muted-foreground">
                          {CATEGORIES.find(c => c.value === ticket.category)?.label}
                        </span>
                      </div>
                      <CardTitle className="text-xl mb-1">{ticket.title}</CardTitle>
                      <CardDescription className="line-clamp-2">{ticket.description}</CardDescription>
                    </div>

                    <div className="flex flex-col items-end gap-2 ml-4">
                      <div className={`flex items-center gap-2 ${statusColor}`}>
                        <StatusIcon className="h-4 w-4" />
                        <span className="text-sm font-medium">{STATUS_CONFIG[ticket.status]?.label}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {ticket.unread_count > 0 && (
                          <Badge className="text-xs bg-red-500 hover:bg-red-600 text-white">
                            <MessageSquare className="h-3 w-3 mr-1" />
                            {ticket.unread_count} nieuw
                          </Badge>
                        )}
                        {ticket.reply_count > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            <MessageSquare className="h-3 w-3 mr-1" />
                            {ticket.reply_count} totaal
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <div className="flex items-center gap-4">
                      <span>Aangemaakt: {formatDate(ticket.created_at)}</span>
                      {ticket.location && <span>📍 {ticket.location}</span>}
                    </div>
                    {ticket.assignee_display_name && (
                      <span>Toegewezen aan: {ticket.assignee_display_name}</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}
